import { useState, useMemo, useEffect, useRef } from "react"
import { X, Check, Search } from "lucide-react"
import taxonomyData from "../../assets/taxonomy.json"

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"
const CATEGORIES = taxonomyData.result
const COMPANIES_ENDPOINT = "https://jobscanner-e1dd4.ue.r.appspot.com/network/companies"
let companiesCache = null

function SelectedChipClose() {
  return (
    <span className="rsm-chip-close" aria-hidden="true">
      <X size={10} strokeWidth={2.4} />
    </span>
  )
}

export default function RoleSelectionModal({
  open,
  onClose,
  selected = [],
  onToggle,
  onClear,
  selectedCompanies = [],
  onCompanyToggle,
  onCompanyClear,
  variant = "modal",
}) {
  const [query, setQuery] = useState("")
  const [activeCatId, setActiveCatId] = useState(CATEGORIES[0]?.id)
  const [companies, setCompanies] = useState(companiesCache || [])
  const [companiesLoading, setCompaniesLoading] = useState(false)
  const inputRef = useRef(null)
  const isDrawer = variant === "drawer"

  useEffect(() => {
    if (!open) return
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 60)
    const esc = e => e.key === "Escape" && onClose?.()
    document.addEventListener("keydown", esc)
    return () => {
      clearTimeout(focusTimer)
      document.removeEventListener("keydown", esc)
    }
  }, [open, onClose])

  useEffect(() => {
    if (!open || !isDrawer) return
    if (companiesCache) {
      setCompanies(companiesCache)
      return
    }
    let cancelled = false
    setCompaniesLoading(true)
    fetch(COMPANIES_ENDPOINT)
      .then(response => response.ok ? response.json() : { companies: [] })
      .then(data => {
        if (cancelled) return
        companiesCache = (data.companies || []).map(company => company.name).filter(Boolean)
        setCompanies(companiesCache)
      })
      .catch(() => { if (!cancelled) setCompanies([]) })
      .finally(() => { if (!cancelled) setCompaniesLoading(false) })
    return () => { cancelled = true }
  }, [open, isDrawer])

  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return CATEGORIES
    return CATEGORIES.filter(cat =>
      cat.second_category.some(sub =>
        sub.third_category.some(item => item.display_name.toLowerCase().includes(q))
      )
    )
  }, [query])

  useEffect(() => {
    if (query.trim() && filteredCategories.length > 0) setActiveCatId(filteredCategories[0].id)
  }, [query, filteredCategories])

  const activeCat = filteredCategories.find(c => c.id === activeCatId) || filteredCategories[0]

  const filteredSubs = useMemo(() => {
    if (!activeCat) return []
    const q = query.trim().toLowerCase()
    return activeCat.second_category
      .map(sub => ({
        ...sub,
        third_category: q
          ? sub.third_category.filter(item => item.display_name.toLowerCase().includes(q))
          : sub.third_category,
      }))
      .filter(sub => sub.third_category.length > 0)
  }, [activeCat, query])

  const selectedRoleIds = useMemo(() => new Set(selected.map(role => role.taxonomyId)), [selected])
  const drawerCategories = useMemo(() => {
    const q = query.trim().toLowerCase()
    return filteredCategories
      .map((cat, catIndex) => {
        const subs = cat.second_category
          .map((sub, subIndex) => {
            const roles = q
              ? sub.third_category.filter(role => role.display_name.toLowerCase().includes(q))
              : sub.third_category
            return {
              ...sub,
              roles,
              selectedCount: roles.filter(role => selectedRoleIds.has(role.id)).length,
              originalIndex: subIndex,
            }
          })
          .filter(sub => sub.roles.length > 0)
          .sort((a, b) => Number(b.selectedCount > 0) - Number(a.selectedCount > 0) || a.originalIndex - b.originalIndex)
        return {
          ...cat,
          subs,
          selectedCount: subs.reduce((count, sub) => count + sub.selectedCount, 0),
          originalIndex: catIndex,
        }
      })
      .filter(cat => cat.subs.length > 0)
      .sort((a, b) => Number(b.selectedCount > 0) - Number(a.selectedCount > 0) || a.originalIndex - b.originalIndex)
  }, [filteredCategories, query, selectedRoleIds])

  const isSelected = id => selected.some(s => s.taxonomyId === id)
  const totalCount = selected.length + selectedCompanies.length
  const companyMatches = query.trim()
    ? companies.filter(name => name.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 12)
    : []

  if (!open) return null

  return (
    <>
      <style>{`
        @keyframes rsm-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes rsm-pop  { from { opacity: 0; transform: translate(-50%,-48%) scale(0.98) } to { opacity: 1; transform: translate(-50%,-50%) scale(1) } }
        @keyframes rsm-slide-in { from { transform: translateX(100%) } to { transform: translateX(0) } }
        @media (max-width: 768px) { .rsm-drawer { width: 100vw !important; } }
        .rsm-chip { transition: background 0.12s, color 0.12s; }
        .rsm-chip:hover { background: #ececec; }
        .rsm-chip.selected {
          height: 32px;
          padding: 0 8px;
          gap: 4px;
          border-radius: 8px;
          background: #00f0a040;
          color: #000000;
        }
        .rsm-chip.selected:hover { background: #00f0a055; }
        .rsm-chip-close {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 16px;
          height: 16px;
          flex-shrink: 0;
          border-radius: 50%;
          background: #ffffff;
          color: #18181b;
        }
        .rsm-cat { transition: background 0.1s; }
        .rsm-cat:hover { background: #fafafa; }
        .rsm-cat.active { background: #f4f4f5; }
        .rsm-cco { transition: background 0.1s; }
        .rsm-cco:hover { background: #fafafa; }
      `}</style>

      {/* Scrim */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 1200,
          background: "rgba(17,17,17,0.42)",
          backdropFilter: "blur(2px)",
          animation: "rsm-fade 0.18s ease",
        }}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Select roles and companies"
        className={isDrawer ? "rsm-drawer" : undefined}
        style={{
          position: "fixed", top: isDrawer ? 0 : "50%",
          left: isDrawer ? undefined : "50%", right: isDrawer ? 0 : undefined,
          transform: isDrawer ? undefined : "translate(-50%,-50%)",
          zIndex: 1201,
          width: isDrawer ? "30vw" : "min(880px, calc(100vw - 32px))",
          height: isDrawer ? "100dvh" : undefined,
          maxHeight: isDrawer ? undefined : "min(640px, calc(100vh - 48px))",
          background: "#ffffff",
          border: "1px solid #ececec",
          borderRadius: isDrawer ? 0 : 16,
          boxShadow: "0 24px 80px rgba(0,0,0,0.18), 0 2px 10px rgba(0,0,0,0.06)",
          display: "flex", flexDirection: "column", overflow: "hidden",
          fontFamily: FF,
          animation: isDrawer ? "rsm-slide-in 0.25s cubic-bezier(0.22,1,0.36,1)" : "rsm-pop 0.2s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "14px 16px",
          borderBottom: "1px solid #f0f0f0",
          flexShrink: 0,
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0,
          }}>
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 28, borderRadius: 8, background: "#f4f4f5", color: "#52525b",
            }}>
              <Search size={14} strokeWidth={2.2} />
            </span>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search roles or companies"
              style={{
                flex: 1, minWidth: 0, border: "none", outline: "none",
                fontSize: 14, fontFamily: FF, color: "#18181b",
                background: "transparent",
              }}
            />
          </div>
          {totalCount > 0 && (
            <button
              onClick={() => { onClear?.(); onCompanyClear?.() }}
              style={{
                fontSize: 11.5, fontWeight: 600, color: "#71717a",
                background: "none", border: "none", cursor: "pointer",
                fontFamily: FF, display: "inline-flex", alignItems: "center", gap: 3,
                padding: "4px 8px", borderRadius: 6,
              }}
            >
              Clear all
            </button>
          )}
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 30, height: 30, borderRadius: 8,
              background: "#f4f4f5", border: "none", cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              color: "#52525b", flexShrink: 0,
            }}
          >
            <X size={14} strokeWidth={2.2} />
          </button>
        </div>

        {/* Body */}
        {isDrawer ? (
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px 24px" }}>
            {drawerCategories.map(cat => {
              return (
                <section key={cat.id} style={{ marginBottom: 22 }}>
                  <h3 style={{ margin: "0 0 12px", fontSize: 13, fontWeight: 700, color: "#18181b" }}>
                    {cat.display_name}
                  </h3>
                  {cat.subs.map(sub => (
                    <div key={sub.id} style={{ marginBottom: 15 }}>
                      <div style={{ marginBottom: 8, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#a1a1aa" }}>
                        {sub.display_name}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {sub.roles.map(role => {
                          const checked = isSelected(role.id)
                          return (
                            <button
                              key={role.id}
                              type="button"
                              onClick={() => onToggle({ taxonomyId: role.id, title: role.display_name })}
                              className={`rsm-chip${checked ? " selected" : ""}`}
                              aria-pressed={checked}
                              style={{
                                display: "inline-flex", alignItems: "center", maxWidth: "100%",
                                gap: checked ? undefined : 5,
                                padding: checked ? undefined : "7px 11px",
                                borderRadius: checked ? undefined : 9999, border: "none",
                                background: checked ? undefined : "#f6f6f7",
                                color: checked ? undefined : "#3f3f46",
                                fontSize: 11.5, fontWeight: checked ? 600 : 500,
                                fontFamily: FF, cursor: "pointer", textAlign: "left",
                              }}
                            >
                              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{role.display_name}</span>
                              {checked && <SelectedChipClose />}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </section>
              )
            })}
            {companyMatches.length > 0 && (
              <section>
                <h3 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#18181b" }}>Companies</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {companyMatches.map(name => {
                    const checked = selectedCompanies.includes(name)
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => onCompanyToggle?.(name)}
                        aria-pressed={checked}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          width: "100%", padding: "9px 10px", border: "none", borderRadius: 8,
                          background: checked ? "#00f0a040" : "transparent",
                          color: checked ? "#000000" : "#3f3f46", textAlign: "left", fontFamily: FF,
                          fontSize: 12.5, cursor: "pointer",
                        }}
                      >
                        {name}
                        {checked && <Check size={14} strokeWidth={2.4} color="#000000" />}
                      </button>
                    )
                  })}
                </div>
              </section>
            )}
            {query.trim() && companiesLoading && (
              <div style={{ fontSize: 11.5, color: "#a1a1aa" }}>Searching companies…</div>
            )}
            {query.trim() && filteredCategories.length === 0 && companyMatches.length === 0 && !companiesLoading && (
              <div style={{ padding: "30px 0", textAlign: "center", color: "#9a9a9a", fontSize: 13 }}>
                No matches for “{query}”
              </div>
            )}
          </div>
        ) : filteredCategories.length === 0 ? (
          <div style={{ padding: "48px 16px", textAlign: "center", color: "#9a9a9a", fontSize: 13 }}>
            No matches for “{query}”
          </div>
        ) : (
          <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
            {/* Left: L1 categories */}
            <div style={{
              width: 200, flexShrink: 0,
              borderRight: "1px solid #f0f0f0",
              overflowY: "auto",
              padding: "6px 0",
            }}>
              {filteredCategories.map(cat => {
                const isCur = cat.id === activeCat?.id
                const selCount = cat.second_category.reduce((a, sub) =>
                  a + sub.third_category.filter(t => isSelected(t.id)).length, 0)
                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCatId(cat.id)}
                    className={`rsm-cat${isCur ? " active" : ""}`}
                    style={{
                      width: "100%",
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      gap: 8,
                      padding: "10px 14px",
                      background: isCur ? "#f4f4f5" : "none",
                      border: "none",
                      borderLeft: isCur ? "2px solid #18181b" : "2px solid transparent",
                      cursor: "pointer", textAlign: "left", fontFamily: FF,
                    }}
                  >
                    <span style={{
                      fontSize: 12.5, lineHeight: 1.3,
                      color: isCur ? "#18181b" : "#52525b",
                      fontWeight: isCur ? 600 : 500,
                      flex: 1, minWidth: 0,
                    }}>
                      {cat.display_name}
                    </span>
                    {selCount > 0 && (
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        color: isCur ? "#ffffff" : "#52525b",
                        background: isCur ? "#18181b" : "#ececec",
                        borderRadius: 9999, padding: "1px 7px",
                        flexShrink: 0,
                      }}>
                        {selCount}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>

            {/* Right: L2 + L3 */}
            <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px 18px" }}>
              {filteredSubs.map(sub => (
                <div key={sub.id} style={{ marginBottom: 18 }}>
                  <p style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
                    textTransform: "uppercase", color: "#a1a1aa", fontFamily: FF,
                    margin: "0 0 10px",
                  }}>
                    {sub.display_name}
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {sub.third_category.map(item => {
                      const sel = isSelected(item.id)
                      return (
                        <button
                          key={item.id}
                          onClick={() => onToggle({ taxonomyId: item.id, title: item.display_name })}
                          className={`rsm-chip${sel ? " selected" : ""}`}
                          style={{
                            display: "inline-flex", alignItems: "center", maxWidth: "100%",
                            gap: sel ? undefined : 5,
                            padding: sel ? undefined : "7px 12px",
                            borderRadius: sel ? undefined : 9999,
                            border: "none",
                            background: sel ? undefined : "#f6f6f7",
                            color: sel ? undefined : "#3f3f46",
                            fontSize: 12, fontWeight: sel ? 600 : 500,
                            fontFamily: FF, cursor: "pointer",
                          }}
                        >
                          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.display_name}</span>
                          {sel && <SelectedChipClose />}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 16px",
          borderTop: "1px solid #f0f0f0",
          background: "#fafafa",
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 12, color: "#71717a", fontFamily: FF }}>
            {totalCount} selected
          </span>
          <button
            onClick={onClose}
            style={{
              display: "inline-flex", alignItems: "center",
              height: 34, padding: "0 18px",
              background: "#18181b", color: "#ffffff",
              border: "none", borderRadius: 9999,
              fontSize: 12.5, fontWeight: 600, fontFamily: FF,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </div>
    </>
  )
}
